/**
 * Payment Run View Component
 * Task 7: Winnie's Payment Workflow (1st Approver)
 * Framework C.4: 6 mandatory DD checks before payment approval
 * Admin-only view accessed via `/payment-run` route
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { emailService } from '../services/emailService';
import { NOTIFICATIONS } from '../config/notifications';
import { notificationsService } from '../services/notificationsService';
import { auditService } from '../services/auditService';
import PaymentHoldControls from './PaymentHoldControls';
import HeldPaymentRuns from './HeldPaymentRuns';
import PayablesPanel from './views/PayablesPanel'
import { User } from '../types';
import './PaymentRunView.css';

interface PaymentRun {
  id: string;
  deal_id: string;
  purchase_price: number;
  holding_deposit: number;
  total_amount: number;
  winnie_review_complete: boolean;
  winnie_reviewed_at?: string;
  status: string;
  deal?: {
    id: string;
    user_id?: string;
    autograb_year: string;
    autograb_make: string;
    autograb_model: string;
    buyer_name: string;
    seller_name: string;
  };
}

interface PaymentRunViewProps {
  user: User;
  paymentRunId?: string;
  onApprovalComplete?: (paymentRunId: string) => void;
}

export const PaymentRunView: React.FC<PaymentRunViewProps> = ({
  user,
  paymentRunId,
  onApprovalComplete
}) => {
  const [paymentRun, setPaymentRun] = useState<PaymentRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // Framework C.4: 6 DD Checks state
  const [checks, setChecks] = useState({
    check_1_identity: false,      // Seller identity verified (DL/Selfie match)
    check_2_documents: false,     // All DD documents validated by DD Reviewer
    check_3_inspection: false,    // Vehicle inspection completed
    check_4_ppsr: false,          // No liens/encumbrances on PPSR
    check_5_price: false,         // Sale price matches contract
    check_6_funds: false          // Buyer funds confirmed
  });

  useEffect(() => {
    loadPaymentRun();
  }, [paymentRunId]);

  const loadPaymentRun = async () => {
    try {
      setLoading(true);
      setError(null);

      // Determine which payment run to load
      let query = supabase
        .from('payment_runs')
        .select(`
          *,
          deal:deals!deal_id(id, user_id, autograb_year, autograb_make, autograb_model, buyer_name, seller_name)
        `)
        .eq('status', 'pending_winnie');

      if (paymentRunId) {
        query = query.eq('id', paymentRunId);
      }

      const { data, error: err } = await query.order('created_at', { ascending: true }).limit(1);

      if (err) throw err;
      if (!data || data.length === 0) {
        setPaymentRun(null);
        setError('No pending payment runs');
        return;
      }

      const run = data[0];
      setPaymentRun(run);

      // Load existing check state if already started
      setChecks({
        check_1_identity: run.winnie_dd_check_1_identity || false,
        check_2_documents: run.winnie_dd_check_2_documents || false,
        check_3_inspection: run.winnie_dd_check_3_inspection || false,
        check_4_ppsr: run.winnie_dd_check_4_ppsr || false,
        check_5_price: run.winnie_dd_check_5_price || false,
        check_6_funds: run.winnie_dd_check_6_funds || false
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load payment run';
      setError(msg);
      console.error('Payment run load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleCheck = (key: keyof typeof checks) => {
    setChecks(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const allChecksComplete = Object.values(checks).every(v => v === true);

  // Winnie, 10 Oct: "Option to select all check as once". Each check is still saved
  // individually on approval, exactly as before - this only saves six clicks.
  const setAllChecks = (value: boolean) => {
    setChecks({
      check_1_identity: value,
      check_2_documents: value,
      check_3_inspection: value,
      check_4_ppsr: value,
      check_5_price: value,
      check_6_funds: value
    });
  };

  const handleApprove = async () => {
    if (!paymentRun) return;

    try {
      setSubmitting(true);
      setError(null);

      // Framework C.4: All 6 checks required
      if (!allChecksComplete) {
        setError('All 6 DD checks must be completed before approval');
        return;
      }

      // Get current user
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error('User not authenticated');

      // Segregation of duties (Framework D.2): the deal creator cannot approve its own payment.
      if (paymentRun.deal?.user_id && paymentRun.deal.user_id === authUser.id) {
        await auditService.log(user, 'segregation_block', {
          details: { stage: '1st_approval', reason: 'creator_cannot_approve', payment_run: paymentRun.id },
        });
        setError('Segregation of duties: you created this deal, so you cannot approve its payment.');
        return;
      }

      // Update payment run
      const { error: updateError } = await supabase
        .from('payment_runs')
        .update({
          winnie_review_complete: true,
          winnie_reviewed_at: new Date().toISOString(),
          winnie_user_id: authUser.id,
          winnie_dd_check_1_identity: checks.check_1_identity,
          winnie_dd_check_2_documents: checks.check_2_documents,
          winnie_dd_check_3_inspection: checks.check_3_inspection,
          winnie_dd_check_4_ppsr: checks.check_4_ppsr,
          winnie_dd_check_5_price: checks.check_5_price,
          winnie_dd_check_6_funds: checks.check_6_funds,
          status: 'pending_tuo'
        })
        .eq('id', paymentRun.id);

      if (updateError) throw updateError;

      // Notify the 2nd approver (Framework Part D: two-person payment authorisation).
      // Non-blocking: a notification failure must not undo the recorded approval.
      try {
        const d = paymentRun.deal;
        const veh = `${d?.autograb_year ?? ''} ${d?.autograb_make ?? ''} ${d?.autograb_model ?? ''}`.trim();
        const approverEmail = await notificationsService.getValue('second_approver_email', NOTIFICATIONS.secondApproverEmail);
        await emailService.sendPaymentApprovalNotification(approverEmail, {
          vehicle: veh || 'vehicle',
          sellerName: d?.seller_name || '',
          totalAmount: paymentRun.total_amount,
          paymentRunLink: `${window.location.origin}/payment-approval`,
        });
      } catch (notifyErr) {
        console.error('2nd approver notification failed (non-blocking):', notifyErr);
      }

      await auditService.log(user, 'payment_1st_approved', { details: { payment_run: paymentRun.id } });
      console.log(`[PAYMENT RUN] Approved by Winnie: ${paymentRun.id}`);

      if (onApprovalComplete) {
        onApprovalComplete(paymentRun.id);
      }

      // Reload next payment run
      setTimeout(() => loadPaymentRun(), 1000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Approval failed';
      setError(msg);
      console.error('Approval error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="payment-run-view">
        <div className="loading">Loading payment runs...</div>
      </div>
    );
  }

  if (!paymentRun) {
    // A load failure used to render as "All payments are up to date", and the
    // only Refresh button lived in the *loaded* branch — so a transient error
    // told the approver everything was clear with no way to try again.
    const failed = !!error && error !== 'No pending payment runs';
    return (
      <div className="payment-run-view">
        <div className="empty-state">
          <h2>{failed ? 'Could not load payment runs' : 'No Pending Payment Runs'}</h2>
          <p>{failed ? error : 'All payments are up to date'}</p>
          <button className="btn btn-secondary" onClick={loadPaymentRun}>Refresh</button>
        </div>
        <HeldPaymentRuns user={user} onChanged={loadPaymentRun} />
        <PayablesPanel user={user} />
      </div>
    );
  }

  const vehicle = paymentRun.deal;
  // Optional chaining inside a template literal rendered the literal string
  // "undefined undefined undefined" whenever the joined deal row came back null.
  const vehicleDescription =
    [vehicle?.autograb_year, vehicle?.autograb_make, vehicle?.autograb_model]
      .filter(Boolean)
      .join(' ') || 'Vehicle details unavailable';
  const formattedAmount = `$${paymentRun.total_amount.toLocaleString('en-AU', { minimumFractionDigits: 2 })}`;

  return (
    <div className="payment-run-view">
      <div className="payment-run-header">
        <h1>Payment Run (AP)</h1>
        <p className="subtitle">Approve a vehicle payment, then work the accounts payable list below</p>
      </div>

      {notice && (
        <div className="alert alert-success" onClick={() => setNotice(null)}>
          <span>{notice}</span>
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
        </div>
      )}

      {/* Deal Summary */}
      <div className="deal-summary card">
        <div className="summary-row">
          <div className="summary-item">
            <label>Vehicle</label>
            <span className="value">{vehicleDescription}</span>
          </div>
          <div className="summary-item">
            <label>Buyer</label>
            <span className="value">{vehicle?.buyer_name}</span>
          </div>
          <div className="summary-item">
            <label>Seller</label>
            <span className="value">{vehicle?.seller_name}</span>
          </div>
        </div>

        <div className="amount-row">
          <div className="amount-item">
            <label>Purchase Price</label>
            <span className="amount">${paymentRun.purchase_price.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div className="amount-item total">
            <label>Total to Pay</label>
            <span className="amount">{formattedAmount}</span>
          </div>
        </div>
      </div>

      {/* Framework C.4: 6 DD Checks - EXACTLY as specified */}
      <div className="dd-checks card">
        <h2>Due Diligence Checks (All Required)</h2>
        <p className="instructions">
          Complete all 6 checks below before approving payment. Each item must be verified.
        </p>

        <div className="check-all-row" style={{ margin: '0 0 0.75rem' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setAllChecks(!allChecksComplete)}
            disabled={submitting}
          >
            {allChecksComplete ? 'Untick all' : 'Tick all 6 checks'}
          </button>
        </div>

        <div className="check-list">
          {/* Check 1 */}
          <label className="check-item">
            <input
              type="checkbox"
              checked={checks.check_1_identity}
              onChange={() => toggleCheck('check_1_identity')}
              disabled={submitting}
            />
            <div className="check-content">
              <span className="check-label">Seller identity verified (DL/Selfie match)</span>
              <span className="check-hint">Driver licence front+back matches selfie ID</span>
            </div>
          </label>

          {/* Check 2 */}
          <label className="check-item">
            <input
              type="checkbox"
              checked={checks.check_2_documents}
              onChange={() => toggleCheck('check_2_documents')}
              disabled={submitting}
            />
            <div className="check-content">
              <span className="check-label">All DD documents validated by DD Reviewer</span>
              <span className="check-hint">All Framework E documents approved</span>
            </div>
          </label>

          {/* Check 3 */}
          <label className="check-item">
            <input
              type="checkbox"
              checked={checks.check_3_inspection}
              onChange={() => toggleCheck('check_3_inspection')}
              disabled={submitting}
            />
            <div className="check-content">
              <span className="check-label">Vehicle inspection completed</span>
              <span className="check-hint">Inspection report received from vendor</span>
            </div>
          </label>

          {/* Check 4 */}
          <label className="check-item">
            <input
              type="checkbox"
              checked={checks.check_4_ppsr}
              onChange={() => toggleCheck('check_4_ppsr')}
              disabled={submitting}
            />
            <div className="check-content">
              <span className="check-label">No liens/encumbrances on PPSR</span>
              <span className="check-hint">PPSR search shows clear ownership</span>
            </div>
          </label>

          {/* Check 5 */}
          <label className="check-item">
            <input
              type="checkbox"
              checked={checks.check_5_price}
              onChange={() => toggleCheck('check_5_price')}
              disabled={submitting}
            />
            <div className="check-content">
              <span className="check-label">Sale price matches contract</span>
              <span className="check-hint">Purchase price confirmed in signed contract</span>
            </div>
          </label>

          {/* Check 6 */}
          <label className="check-item">
            <input
              type="checkbox"
              checked={checks.check_6_funds}
              onChange={() => toggleCheck('check_6_funds')}
              disabled={submitting}
            />
            <div className="check-content">
              <span className="check-label">Buyer funds confirmed</span>
              <span className="check-hint">Bank transfer funds or deposit verified</span>
            </div>
          </label>
        </div>

        {/* Progress indicator */}
        <div className="check-progress">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${(Object.values(checks).filter(v => v).length / 6) * 100}%` }}
            ></div>
          </div>
          <p className="progress-text">
            {Object.values(checks).filter(v => v).length} of 6 checks completed
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="actions">
        <button
          className="btn btn-primary"
          onClick={handleApprove}
          disabled={!allChecksComplete || submitting}
        >
          {submitting ? 'Approving...' : 'Approve Payment'}
        </button>
        <button
          className="btn btn-secondary"
          onClick={loadPaymentRun}
          disabled={submitting}
        >
          Refresh
        </button>
      </div>

      <PaymentHoldControls
        runId={paymentRun.id}
        stage="first_approval"
        user={user}
        disabled={submitting}
        onDone={(msg) => {
          setError(null);
          setNotice(msg);
          loadPaymentRun();
        }}
      />

      <HeldPaymentRuns user={user} onChanged={loadPaymentRun} />
      <PayablesPanel user={user} />

      {/* Framework C.4 notes */}
      <div className="framework-note card">
        <p>
          <strong>Framework Note:</strong> Payment must be approved by both the 1st Approver and the
          2nd Approver before release to Westpac. After your approval an email goes to the 2nd
          approver for final sign-off. If something does not look right, hold or reject the run
          rather than approving it — both record a reason on the audit trail.
        </p>
      </div>
    </div>
  );
};

export default PaymentRunView;
